import React, { useState, useEffect } from 'react';
import { useQuill } from 'react-quilljs';
import 'quill/dist/quill.snow.css';
import Quill from 'quill';
import ImageResize from 'quill-image-resize';
import { db, storage } from '../../firebase/firebase';
import { ref as dbRef, set, onValue, remove } from 'firebase/database';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { useParams } from 'react-router-dom';
import { CircularProgress } from '@mui/material';

Quill.register('modules/imageResize', ImageResize);

export default function EditPost() {
    const { id } = useParams();
    const [content, setContent] = useState('');
    const [postTitle, setPostTitle] = useState('');
    const [loading, setLoading] = useState(false);
    const [prevImg, setPrevImg] = useState('');
    const [prevImgFile, setPrevImgFile] = useState(null);

    const { quill, quillRef } = useQuill({
        theme: 'snow',
        modules: {
            imageResize: {},
            toolbar: [
                [{ header: [1, 2, 3, 4, 5, 6, false] }],
                ['bold', 'italic', 'underline', 'strike'],
                [{ list: 'ordered' }, { list: 'bullet' }],
                [{ script: 'sub' }, { script: 'super' }],
                [{ indent: '-1' }, { indent: '+1' }],
                [{ direction: 'rtl' }],
                [{ size: ['small', false, 'large', 'huge'] }],
                [{ color: [] }, { background: [] }],
                [{ font: [] }],
                [{ align: [] }],
                ['clean'],
                ['link', 'image'],
            ],
        },
    });

    useEffect(() => {
        const postRef = dbRef(db, `posts/en/${id}`);
        onValue(postRef, (snapshot) => {
            const data = snapshot.val();
            if (data) {
                setContent(data.content || '');
                setPrevImg(data.imgPreview || '');
                setPostTitle(data.postTitle || '');
            }
        });
    }, [id]);

    useEffect(() => {
        if (quill && content) {
            quill.clipboard.dangerouslyPasteHTML(content);
        }
    }, [quill, content]);

    useEffect(() => {
        if (quill) {
            quill.getModule('toolbar').addHandler('image', imageHandler);
            quill.getModule('toolbar').addHandler('video', videoHandler);
        }
    }, [quill]);

    const imageHandler = () => {
        const input = document.createElement('input');
        input.setAttribute('type', 'file');
        input.setAttribute('accept', 'image/*');
        input.onchange = async () => {
            const file = input.files[0];
            if (file) {
                const range = quill.getSelection(true);
                const reader = new FileReader();
                reader.onload = () => {
                    quill.insertEmbed(range.index, 'image', reader.result);
                };
                reader.readAsDataURL(file);
            }
        };
        input.click();
    };

    const videoHandler = () => {
        const input = document.createElement('input');
        input.setAttribute('type', 'file');
        input.setAttribute('accept', 'video/*');
        input.onchange = async () => {
            const file = input.files[0];
            if (file) {
                const range = quill.getSelection(true);
                const reader = new FileReader();
                reader.onload = () => {
                    const videoEmbed = `
                        <video controls style="max-width: 100%; height: auto;">
                            <source src="${reader.result}" type="${file.type}">
                            Your browser does not support the video tag.
                        </video>`;
                    quill.clipboard.dangerouslyPasteHTML(range.index, videoEmbed);
                };
                reader.readAsDataURL(file);
            }
        };
        input.click();
    };

    const handleImageChange = (event) => {
        const file = event.target.files[0];
        if (file && file.type.startsWith('image')) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setPrevImg(reader.result);
                setPrevImgFile(file);
            };
            reader.readAsDataURL(file);
        } else {
            setPrevImg('');
            setPrevImgFile(null);
        }
    };

    const handleSubmit = async () => {
        if (postTitle && (prevImgFile || prevImg)) {
            setLoading(true);

            let previewImageUrl = prevImg;
            if (prevImgFile) {
                const previewPath = `images/en/${Date.now()}_${prevImgFile.name}`;
                const previewRef = storageRef(storage, previewPath);
                const previewSnapshot = await uploadBytes(previewRef, prevImgFile);
                previewImageUrl = await getDownloadURL(previewSnapshot.ref);
            }

            const contentHtml = quill.root.innerHTML;

            try {
                await set(dbRef(db, `posts/en/${id}`), {
                    postTitle,
                    imgPreview: previewImageUrl,
                    content: contentHtml,
                    timestamp: Date.now(),
                });
                alert('Post updated successfully!');
            } catch (error) {
                console.error('Error updating post: ', error);
            } finally {
                setLoading(false);
            }
        }else{
            alert('Post title and preview image are empty.')
        }
    };

    const handleDelete = async ()=>{
        try {
            const postRef = dbRef(db, `posts/en/${id}`);
            await remove(postRef);
            alert('Post deleted successfully!');
        } catch (error) {
            console.error('Error deleting post: ', error);
            alert('Failed to delete the post.');
        }
    }

    return (
        <div>
            {loading && <div className='bkdrop'><CircularProgress sx={{ color: '#fff' }} /></div>}
            <button
                className='submit-post-button'
                onClick={handleSubmit}
                style={{
                    display: 'inline-block',
                    backgroundColor: '#0086a1',
                    color: '#fff',
                    padding: '5px 20px',
                    fontSize: '13pt',
                    border: 'none',
                    marginBottom: '10px',
                    marginRight: '30px',
                    fontFamily: 'Arial',
                    borderRadius: '10px',
                    fontWeight: 'bold',
                    boxShadow: '0px 0px 5px .5px gray',
                }}
            >
                Save Changes
            </button>
            <input
                type="text"
                placeholder='Post Title'
                value={postTitle}
                onChange={(e) => setPostTitle(e.target.value)}
                style={{
                    display: 'inline-block',
                    fontSize: '13pt',
                    padding: '5px 10px',
                    outline: 'none',
                    border: '2px solid #0086a1',
                    borderRadius: '5px',
                    width: '400px',
                    fontFamily: 'Arial',
                }}
            />
            <input
                onChange={handleImageChange}
                type="file"
                id='chooseimg'
                style={{ display: 'none' }}
                accept='images/*'
            />
            <label
                htmlFor="chooseimg"
                style={{
                    display: 'inline-block',
                    fontSize: '13pt',
                    fontFamily: 'Arial',
                    backgroundColor: '#0086a1',
                    color: 'white',
                    padding: '5px 20px',
                    cursor: 'pointer',
                    marginLeft: '100px',
                    borderRadius: '5px',
                }}
            >
                Choose Image
            </label>
            <img
                src={prevImg}
                alt=""
                style={{
                    display: 'inline-block',
                    height: '65px',
                    marginLeft: '20px',
                    position: 'fixed',
                    top: '3px',
                }}
            />
            <button
                className='submit-post-button'
                onClick={handleDelete}
                style={{
                    display: 'inline-block',
                    backgroundColor: '#d00',
                    color: '#fff',
                    padding: '5px 20px',
                    fontSize: '13pt',
                    border: 'none',
                    marginBottom: '10px',
                    marginRight: '30px',
                    fontFamily: 'Arial',
                    borderRadius: '10px',
                    fontWeight: 'bold',
                    boxShadow: '0px 0px 5px .5px gray',
                    float: 'right'
                }}
            >
                Delete Post
            </button>
            <div
                className='custom-quill'
                style={{
                    minHeight: 'calc(100vh - 250px)',
                }}
                ref={quillRef}
            />
        </div>
    );
}
